import {
  Center,
  Flex,
  FlexProps,
  Spinner,
  Text,
  TextProps,
} from "@chakra-ui/react";
import React from "react";

import { AudienceCountUnavailable } from "@/common/components/AudienceCountUnavailable";
import { useTargetingEstimation } from "@/common/data/useTargetingEstimation";
import { formatLargeNumber } from "@/common/utils/numberFormatting";
import { FeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import pluralize from "@reflag/shared/utils/pluralize";

const sharedEstimationTextProps = {
  as: "span",
  color: "dimmed",
  fontSize: "sm",
  lineHeight: "21px",
} satisfies TextProps;

type RulesChangesEstimationProps = FlexProps & {
  currentTargeting: FeatureTargeting;
  newTargeting?: FeatureTargeting;
  flagKey: string;
};

function DiffText({ added, removed }: { added?: number; removed?: number }) {
  if (added === 0 && removed === 0) {
    return null;
  }

  return (
    <>
      {"("}
      <Text as="span" color="green">
        {added ? `+${formatLargeNumber(added)}` : null}
      </Text>
      {added && removed ? " " : null}
      <Text as="span" color="red">
        {removed ? `-${formatLargeNumber(removed)}` : null}
      </Text>
      {")"}
    </>
  );
}

export const RulesChangesEstimation = ({
  currentTargeting,
  newTargeting,
  flagKey,
  ...rest
}: RulesChangesEstimationProps) => {
  const {
    data: currentEstimate,
    isLoading: isLoadingCurrent,
    isSuccess: isSuccessCurrent,
  } = useTargetingEstimation(currentTargeting, flagKey);

  const {
    data: newEstimate,
    isLoading: isLoadingNew,
    isSuccess: isSuccessNew,
  } = useTargetingEstimation(newTargeting, flagKey, !!newTargeting);

  const isLoading = isLoadingCurrent || (newTargeting && isLoadingNew);
  const isSuccess = isSuccessCurrent && (!newTargeting || isSuccessNew);

  const displayedEstimate = newTargeting ? newEstimate : currentEstimate;
  const hasUnavailableCount =
    displayedEstimate?.companyCount === null ||
    displayedEstimate?.userCount === null;

  const showCounts = {
    companyCount: displayedEstimate?.companyCount ?? 0,
    userCount: displayedEstimate?.userCount ?? 0,
  };

  const canShowDiff =
    !!newTargeting &&
    currentEstimate?.companyCount != null &&
    currentEstimate.userCount != null &&
    newEstimate?.companyCount != null &&
    newEstimate.userCount != null;

  const getDiffs = () => {
    const currentCompanyCount = currentEstimate?.companyCount;
    const currentUserCount = currentEstimate?.userCount;
    const newCompanyCount = newEstimate?.companyCount;
    const newUserCount = newEstimate?.userCount;

    if (
      !newTargeting ||
      currentCompanyCount == null ||
      currentUserCount == null ||
      newCompanyCount == null ||
      newUserCount == null
    ) {
      return {
        companiesAdded: 0,
        companiesRemoved: 0,
        usersAdded: 0,
        usersRemoved: 0,
      };
    }

    // Calculate added/removed (approximation based on count differences)
    return {
      companiesAdded: Math.max(0, newCompanyCount - currentCompanyCount),
      companiesRemoved: Math.max(0, currentCompanyCount - newCompanyCount),
      usersAdded: Math.max(0, newUserCount - currentUserCount),
      usersRemoved: Math.max(0, currentUserCount - newUserCount),
    };
  };
  const { companiesAdded, companiesRemoved, usersAdded, usersRemoved } =
    getDiffs();

  if (isLoading) {
    return (
      <Center>
        <Spinner
          aria-label="Loading estimation"
          role="status"
          variant="subtle"
        />
      </Center>
    );
  }

  if (isSuccess) {
    if (hasUnavailableCount) {
      return (
        <Flex h="full" justifyContent="center" {...rest}>
          <AudienceCountUnavailable />
        </Flex>
      );
    }

    return (
      <Flex gap={1} {...rest}>
        <Text {...sharedEstimationTextProps}>
          {formatLargeNumber(showCounts.userCount)}{" "}
          {pluralize("known user", showCounts.userCount)}{" "}
          {canShowDiff && (
            <DiffText added={usersAdded} removed={usersRemoved} />
          )}
        </Text>
        <Text {...sharedEstimationTextProps}>
          {formatLargeNumber(showCounts.companyCount)}{" "}
          {pluralize(
            "known company",
            showCounts.companyCount,
            "known companies",
          )}{" "}
          {canShowDiff && (
            <DiffText added={companiesAdded} removed={companiesRemoved} />
          )}
        </Text>
      </Flex>
    );
  }

  return <Text {...sharedEstimationTextProps}>0 companies</Text>;
};
